import { createDefaultJointInput } from "./jointDefaults";
import {
  getBodyVisibilityUiState,
  setBodyVisibilityUiState,
} from "../selected-body-visibility/visibilityEvents";
import { useSelectionStore } from "../stores/selectionStore";
import { logger } from "./logger";

const DEFAULT_GEOM_SIZE_BY_KIND = {
  box: [0.05, 0.05, 0.05],
  sphere: [0.05],
  capsule: [0.03, 0.05],
  cylinder: [0.05, 0.05],
};

let autoBodyLabelMarkerUuid = null;
let unsubscribeAutoBodyLabelMarkerSelection = null;

function selectionIncludesBody(selection, bodyUuid) {
  return selection?.selectedBody === bodyUuid || selection?.selectedBodies?.includes?.(bodyUuid);
}

function clearAutoBodyLabelMarkerTracking() {
  autoBodyLabelMarkerUuid = null;
  if (unsubscribeAutoBodyLabelMarkerSelection) {
    unsubscribeAutoBodyLabelMarkerSelection();
    unsubscribeAutoBodyLabelMarkerSelection = null;
  }
}

function showBodyLabelMarkerForNewBody(bodyUuid) {
  if (!bodyUuid) return;
  const wasEnabled = getBodyVisibilityUiState().bodyLabelMarkers;
  if (!wasEnabled) {
    setBodyVisibilityUiState({ bodyLabelMarkers: true });
  }

  clearAutoBodyLabelMarkerTracking();
  if (wasEnabled) return;

  autoBodyLabelMarkerUuid = bodyUuid;
  unsubscribeAutoBodyLabelMarkerSelection = useSelectionStore.subscribe((selection) => {
    const targetBodyUuid = autoBodyLabelMarkerUuid;
    if (!targetBodyUuid || selectionIncludesBody(selection, targetBodyUuid)) return;
    setBodyVisibilityUiState({ bodyLabelMarkers: false });
    clearAutoBodyLabelMarkerTracking();
  });
}

export function addDefaultBody(
  sceneActions,
  parentBodyUuid = null,
  position = [0, 0, 0],
  options = {}
) {
  const bodyUuid = sceneActions.addBody(
    { name: "body", position, orientation: [1, 0, 0, 0] },
    parentBodyUuid
  );
  if (!parentBodyUuid && options.addFreeJoint !== false) {
    addDefaultJoint(sceneActions, bodyUuid, "free");
  }
  showBodyLabelMarkerForNewBody(bodyUuid);
  return bodyUuid;
}

export function addDefaultJoint(sceneActions, parentBodyUuid, kind) {
  if (!parentBodyUuid || !kind) return null;
  const input = createDefaultJointInput(kind);
  return sceneActions.addJoint(input, parentBodyUuid);
}

export function addDefaultSite(sceneActions, parentBodyUuid, position = [0, 0, 0]) {
  return sceneActions.addSite(
    {
      position,
      orientation: [1, 0, 0, 0],
      size: [0.02, 0.02, 0.02],
      kind: "sphere",
      color: "#ffff00",
    },
    parentBodyUuid ?? null
  );
}

export function addDefaultInertia(sceneActions, parentBodyUuid) {
  if (!parentBodyUuid) return null;
  return sceneActions.addInertia(parentBodyUuid, {
    name: "inertial",
    mass: 1.0,
    position: [0, 0, 0],
    orientation: [1, 0, 0, 0],
    diagonal: [0.001, 0.001, 0.001],
  });
}

export function addDefaultCollisionGeom(sceneActions, parentBodyUuid, kind) {
  if (!parentBodyUuid || !kind) return null;
  return sceneActions.addGeom(
    {
      name: `geom_${kind}`,
      kind,
      position: [0, 0, 0],
      orientation: [1, 0, 0, 0],
      size: DEFAULT_GEOM_SIZE_BY_KIND[kind] ?? [0.05, 0.05, 0.05],
      materials: [],
      visualOnly: false,
    },
    parentBodyUuid
  );
}

export function addDefaultVisualGeom(sceneActions, parentBodyUuid, kind) {
  if (!parentBodyUuid || !kind) return null;
  if (kind === "mesh") {
    logger.warn(
      "[bodyAddActions] Refusing to create a visual mesh geom without an assigned mesh asset."
    );
    return null;
  }
  return sceneActions.addGeom(
    {
      name: `visual_${kind}`,
      kind,
      position: [0, 0, 0],
      orientation: [1, 0, 0, 0],
      size: DEFAULT_GEOM_SIZE_BY_KIND[kind] ?? [0.05, 0.05, 0.05],
      materials: [],
      visualOnly: true,
    },
    parentBodyUuid
  );
}

export function addMeshGeomToBody(
  sceneActions,
  parentBodyUuid,
  meshName,
  meshUrl,
  fingerprint = undefined
) {
  if (!parentBodyUuid || !meshUrl) return null;
  const meshUuid = sceneActions.addMesh({
    name: meshName || "mesh",
    url: meshUrl,
    ...(fingerprint !== undefined ? { fingerprint } : {}),
  });
  sceneActions.addGeom(
    {
      name: meshName || "mesh",
      kind: "mesh",
      position: [0, 0, 0],
      orientation: [1, 0, 0, 0],
      size: [1, 1, 1],
      mesh: meshUuid,
      materials: [],
      visualOnly: true,
    },
    parentBodyUuid
  );
  return meshUuid;
}
